import React, { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import ContactForm from './components/ContactForm';

const SmartPBDPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('smart-pbd');
  const [showContactForm, setShowContactForm] = useState(false);

  const menuItems = [
    { id: 'overview', label: '통합 자동화 계측', href: '/overview' },
    { id: 'smart-pbd', label: '스마트 P.B.D. 통합관리', href: '/smart-pbd' },
    { id: 'smart-dcm', label: '스마트 DCM 통합관리', href: '/smart-dcm' },
    { id: 'gnss-settlement', label: '지반침하 GNSS 무선 침하계', href: '/gnss-settlement' }
  ];

  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: '실시간 모니터링',
      description: 'P.B.D. 시스템의 실시간 상태 감시 및 이상 감지'
    },
    {
      icon: 'ri-settings-3-line',
      title: '자동 제어',
      description: '지능형 알고리즘을 통한 자동 운영 및 최적화'
    },
    {
      icon: 'ri-line-chart-line',
      title: '성능 최적화',
      description: '데이터 분석을 통한 시스템 성능 향상'
    },
    {
      icon: 'ri-smartphone-line',
      title: '원격 관리',
      description: '모바일 앱을 통한 언제 어디서나 시스템 관리'
    }
  ];

  const monitoringFeatures = [
    {
      icon: 'ri-ruler-line',
      title: '수직도 모니터링',
      description: '실시간 수직도 측정 및 편차 알림',
      color: 'bg-blue-500'
    },
    {
      icon: 'ri-map-pin-line',
      title: '타공 위치 추적',
      description: '정확한 타공 위치 확인 및 기록',
      color: 'bg-green-500'
    },
    {
      icon: 'ri-dashboard-line',
      title: '심도·하중·압력',
      description: '다중 센서를 통한 종합 데이터 수집',
      color: 'bg-purple-500'
    },
    {
      icon: 'ri-map-2-line',
      title: '평면도 표시',
      description: '2D/3D 평면도 실시간 업데이트',
      color: 'bg-orange-500'
    },
    {
      icon: 'ri-truck-line',
      title: '장비 위치 추적',
      description: 'GPS 기반 장비 실시간 위치 확인',
      color: 'bg-red-500'
    }
  ];

  const systemFeatures = [
    {
      number: '01',
      title: '한 화면에서 끝나는 올인원 시공 모니터링',
      description: '수직도, 타공 위치, 심도·하중·압력, 평면도, 장비 위치를 메인 화면에서 동시에 확인 가능',
      benefit: '장비·관리자 모두 즉시 판단 가능',
      icon: 'ri-dashboard-3-line',
      color: 'from-blue-500 to-blue-600',
      image: 'https://public.readdy.ai/ai/img_res/f3823cc448fcf40a794f44c94981432a.jpg'
    },
    {
      number: '02',
      title: '실시간 품질관리 자동 체크',
      description: '수직도·타공 위치를 허용오차 내/외로 색 구분 및 그래프 표시',
      benefit: '목표 좌표와 실제 좌표 오차, 심도·하중 변화가 실시간으로 시각화되어 부적합 공 즉시 식별 가능',
      icon: 'ri-check-double-line',
      color: 'from-green-500 to-green-600',
      image: 'https://readdy.ai/api/search-image?query=Real-time%20quality%20control%20system%20for%20construction%2C%20color-coded%20tolerance%20monitoring%2C%20verticality%20and%20position%20error%20visualization%2C%20automated%20quality%20check%20interface%2C%20green%20and%20white%20color%20scheme%2C%20professional%20quality%20management%20dashboard&width=600&height=400&seq=quality-control&orientation=landscape'
    },
    {
      number: '03',
      title: '현장 맞춤 자동기록 기능으로 시공 이력 완전 관리',
      description: '공번호별 설계 좌표·설계심도·시공심도·오차·실입하중 등이 자동 저장',
      benefit: '타공 목표 오차 이내 진입 시 자동 기록 시작 등 조건부 기록 설정 가능 → 누락·오기록 최소화',
      icon: 'ri-file-list-3-line',
      color: 'from-purple-500 to-purple-600',
      image: 'https://readdy.ai/api/search-image?query=Automated%20construction%20record%20management%20system%2C%20drilling%20history%20tracking%2C%20coordinate%20and%20depth%20recording%2C%20construction%20data%20management%20interface%2C%20purple%20and%20white%20color%20scheme%2C%20professional%20record%20keeping%20system&width=600&height=400&seq=auto-recording&orientation=landscape'
    },
    {
      number: '04',
      title: 'AutoCAD 연동을 통한 정밀 공관리',
      description: '도면에서 PBD 공을 선택하면 공번호·좌표를 자동 추출하는 CAD 공 관리자 기능 제공',
      benefit: '대규모 현장에서 수작업 입력 없이 좌표·공 정렬 관리가 가능해 오차와 인력 소모를 줄임',
      icon: 'ri-draft-line',
      color: 'from-orange-500 to-orange-600',
      image: 'https://readdy.ai/api/search-image?query=AutoCAD%20integration%20for%20construction%20management%2C%20CAD%20drawing%20with%20PBD%20hole%20selection%2C%20coordinate%20extraction%20interface%2C%20precise%20hole%20management%20system%2C%20orange%20and%20white%20color%20scheme%2C%20professional%20CAD%20integration%20software&width=600&height=400&seq=autocad-integration&orientation=landscape'
    },
    {
      number: '05',
      title: 'GNSS·센서·유량계까지 통합 설정하는 진짜 통합 시스템',
      description: 'GNSS 설정, 센서 채널 및 수집 간격, 자동 기록 조건, 평면도 표출, 유량계 시리얼 포트 등 현장 환경에 맞춘 세부 설정 지원',
      benefit: '하나의 플랫폼에서 계측·위치·장비 정보를 모두 관리',
      icon: 'ri-settings-4-line',
      color: 'from-red-500 to-red-600',
      image: 'https://readdy.ai/api/search-image?query=Integrated%20system%20configuration%20for%20GNSS%20sensors%20and%20flow%20meters%2C%20comprehensive%20system%20settings%20interface%2C%20unified%20platform%20management%2C%20red%20and%20white%20color%20scheme%2C%20professional%20integration%20control%20panel&width=600&height=400&seq=integrated-system&orientation=landscape'
    },
    {
      number: '06',
      title: '센서 교정 기능으로 신뢰도 보장',
      description: '각 센서의 계측값·Gain·초기값 확인 및 교정 메뉴 제공',
      benefit: '현장에서 즉시 영점 및 보정 가능 → 데이터 신뢰성 확보',
      icon: 'ri-tools-line',
      color: 'from-teal-500 to-teal-600',
      image: 'https://readdy.ai/api/search-image?query=Sensor%20calibration%20system%20for%20construction%20monitoring%2C%20calibration%20interface%20with%20measurement%20values%20and%20gain%20settings%2C%20sensor%20reliability%20assurance%2C%20teal%20and%20white%20color%20scheme%2C%20professional%20calibration%20software&width=600&height=400&seq=sensor-calibration&orientation=landscape'
    },
    {
      number: '07',
      title: '보고서·도면 출력까지 원클릭',
      description: '일일 통계보고서, 타공별 보고서 저장, 도면 업·다운로드, DXF 저장 지원',
      benefit: '발주처 제출용 자료를 시스템에서 바로 생성 가능',
      icon: 'ri-file-download-line',
      color: 'from-indigo-500 to-indigo-600',
      image: 'https://readdy.ai/api/search-image?query=One-click%20report%20generation%20system%2C%20automated%20construction%20reports%20and%20drawings%20export%2C%20document%20management%20interface%2C%20indigo%20and%20white%20color%20scheme%2C%20professional%20reporting%20software&width=600&height=400&seq=report-generation&orientation=landscape'
    },
    {
      number: '08',
      title: '사용자 친화적 UI와 음성 안내/시뮬레이션 지원',
      description: '직관적인 아이콘, 그래프 중심 구성, 음성 안내 옵션, 시뮬레이션 시작 위치 설정 등',
      benefit: '신규 사용자도 빠르게 적응 가능',
      icon: 'ri-user-smile-line',
      color: 'from-pink-500 to-pink-600',
      image: 'https://readdy.ai/api/search-image?query=User-friendly%20construction%20management%20interface%2C%20intuitive%20icons%20and%20graphs%2C%20voice%20guidance%20and%20simulation%20features%2C%20pink%20and%20white%20color%20scheme%2C%20modern%20user%20interface%20design&width=600&height=400&seq=user-friendly-ui&orientation=landscape'
    }
  ];

  const technologies = [
    {
      name: 'IoT 센서 네트워크',
      description: '고정밀 센서를 통한 실시간 P.B.D. 상태 모니터링',
      image: 'https://readdy.ai/api/search-image?query=IoT%20sensor%20network%20for%20PBD%20system%20monitoring%2C%20wireless%20sensors%20installed%20on%20industrial%20equipment%2C%20real-time%20data%20collection%20devices%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20industrial%20setup%2C%20modern%20monitoring%20technology&width=400&height=300&seq=pbd-iot-sensors&orientation=landscape'
    },
    {
      name: '지능형 제어 시스템',
      description: 'AI 기반 자동 제어 및 최적화 알고리즘',
      image: 'https://readdy.ai/api/search-image?query=Intelligent%20control%20system%20for%20PBD%20management%2C%20AI-powered%20control%20panel%2C%20automated%20industrial%20control%20interface%2C%20blue%20and%20white%20color%20scheme%2C%20modern%20control%20room%20setup%2C%20advanced%20automation%20technology&width=400&height=300&seq=pbd-control-system&orientation=landscape'
    },
    {
      name: '데이터 분석 플랫폼',
      description: '빅데이터 기반 성능 분석 및 예측 모델링',
      image: 'https://readdy.ai/api/search-image?query=Data%20analytics%20platform%20for%20PBD%20system%2C%20big%20data%20visualization%20dashboard%2C%20performance%20analysis%20charts%2C%20predictive%20modeling%20interface%2C%20blue%20and%20white%20color%20scheme%2C%20modern%20analytics%20software&width=400&height=300&seq=pbd-analytics&orientation=landscape'
    },
    {
      name: '모바일 관리 앱',
      description: '스마트폰을 통한 원격 모니터링 및 제어',
      image: 'https://readdy.ai/api/search-image?query=Mobile%20app%20for%20PBD%20system%20management%2C%20smartphone%20displaying%20industrial%20control%20interface%2C%20remote%20monitoring%20application%2C%20blue%20and%20white%20color%20scheme%2C%20modern%20mobile%20UI%20design%2C%20professional%20app%20interface&width=400&height=300&seq=pbd-mobile-app&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="flex">
        {/* 왼쪽 사이드 메뉴바 */}
        <aside className="w-80 bg-white shadow-lg border-r border-gray-200 min-h-screen sticky top-16">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Product</h2>
            <nav className="space-y-2">
              {menuItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  className={`block px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                    activeSection === item.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  onClick={() => setActiveSection(item.id)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* 메인 콘텐츠 */}
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-green-600 to-green-800 text-white">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-5xl font-bold mb-6">
                  스마트 P.B.D. 통합관리 시스템
                </h1>
                <p className="text-xl mb-8 leading-relaxed">
                  첨단 IoT 기술과 AI 알고리즘을 활용하여 P.B.D. 시스템을 
                  지능적으로 모니터링하고 최적화하는 혁신적인 통합 관리 솔루션입니다.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <i className={`${benefit.icon} text-2xl`}></i>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-sm opacity-90">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 올인원 시공 모니터링 섹션 */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  올인원 시공 모니터링
                </h2>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
                  한 화면에서 끝나는 통합 모니터링 시스템으로 수직도, 타공 위치, 심도·하중·압력, 평면도, 장비 위치를 
                  메인 화면에서 동시에 확인 가능하여 장비·관리자 모두 즉시 판단할 수 있습니다.
                </p>
              </div>

              {/* 실시간 모니터링 대시보드 */}
              <section className="py-6 bg-gray-50">
                <div className="container mx-auto px-6">
                  <section className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="p-8">
                      <div className="mb-12">
                        <img
                          src="https://static.readdy.ai/image/62bfd8064e11f74310ce1cc00b80d48a/8f3dc7f07725e5f67be6de6117ac76c1.png"
                          alt="PBD 실시간 모니터링 대시보드"
                          className="w-full h-auto rounded-lg shadow-lg object-top"
                        />
                      </div>
                    </div>
                  </section>
                </div>
              </section>

              {/* 모니터링 기능 카드들 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
                {monitoringFeatures.map((feature, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                    <div className={`w-16 h-16 ${feature.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <i className={`${feature.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>

              {/* 핵심 장점 */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">
                    올인원 모니터링의 핵심 장점
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-eye-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">즉시 판단 가능</h4>
                    <p className="text-gray-600">
                      모든 정보를 한 화면에서 확인하여 신속한 의사결정과 즉각적인 대응이 가능합니다.
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-time-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">시간 절약</h4>
                    <p className="text-gray-600">
                      여러 시스템을 오가며 확인할 필요 없이 통합된 인터페이스로 작업 효율성을 극대화합니다.
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-shield-check-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">안전성 향상</h4>
                    <p className="text-gray-600">
                      실시간 종합 모니터링으로 위험 요소를 사전에 감지하고 예방할 수 있습니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 시스템 특징 섹션 */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  시스템 특징
                </h2>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                  스마트 P.B.D. 통합관리 시스템의 8가지 핵심 특징으로 
                  시공 품질과 효율성을 혁신적으로 향상시킵니다.
                </p>
              </div>

              <div className="space-y-16">
                {systemFeatures.map((feature, index) => (
                  <div key={index} className={`${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex flex-col lg:flex gap-12 items-center`}>
                    <div className="lg:w-1/2">
                      <div className="flex items-center mb-6">
                        <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center mr-6`}>
                          <i className={`${feature.icon} text-2xl text-white`}></i>
                        </div>
                        <div className={`text-6xl font-bold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                          {feature.number}
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-800 mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                        {feature.description}
                      </p>
                      <div className={`bg-gradient-to-r ${feature.color} bg-opacity-10 p-4 rounded-lg border-l-4 border-gradient-to-b ${feature.color.replace('from-', 'border-').replace(' to-' + feature.color.split(' to-')[1], '')}`}>
                        <div className="flex items-start">
                          <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                          <p className="text-gray-700 font-medium">
                            {feature.benefit}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="lg:w-1/2">
                      <div 
                        className="h-80 bg-cover bg-center rounded-xl shadow-lg"
                        style={{ backgroundImage: `url(${feature.image})` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 올인원 모니터링 핵심 장점 */}
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">올인원 모니터링의 핵심 장점</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-eye-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">즉시 판단 가능</h4>
                    <p className="text-gray-600">모든 정보를 한 화면에서 확인하여 신속한 의사결정과 즉각적인 대응이 가능합니다.</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-time-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">시간 절약</h4>
                    <p className="text-gray-600">여러 시스템을 오가며 확인할 필요 없이 통합된 인터페이스로 작업 효율성을 극대화합니다.</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-shield-check-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">안전성 향상</h4>
                    <p className="text-gray-600">실시간 종합 모니터링으로 위험 요소를 사전에 감지하고 예방할 수 있습니다.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 실시간 모니터링 대시보드 */}
          <section className="py-6 bg-white">
            <div className="container mx-auto px-6">
              <div 
                className="h-96 bg-cover bg-center rounded-lg shadow-lg"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Comprehensive%20PBD%20construction%20monitoring%20dashboard%20showing%20verticality%20drilling%20position%20depth%20load%20pressure%20floor%20plan%20equipment%20location%20all%20in%20one%20screen%2C%20integrated%20construction%20management%20interface%2C%20orange%20and%20white%20color%20scheme%2C%20professional%20monitoring%20system&width=1200&height=600&seq=pbd-dashboard&orientation=landscape')`
                }}
              ></div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-green-600 text-white">
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-4xl font-bold mb-6">
                스마트 P.B.D. 통합관리 시스템을 경험해보세요
              </h2>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                P.B.D. 시스템의 효율성과 안정성을 한 단계 높여줄 혁신적인 관리 솔루션을 지금 바로 만나보세요.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setShowContactForm(true)}
                  className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  무료 상담 신청
                </button>
                <a 
                  href="/system" 
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  시스템 구성 보기
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
      {showContactForm && <ContactForm onClose={() => setShowContactForm(false)} />}
    </div>
  );
};

export default SmartPBDPage;